<template>
    <div class="msg-card" :class="cardClass" :style="cardStyle" @click="showChat">
        <div class="msg-thumb">
            <img v-if="hasAvatar" :src="avatar" :alt="participants">
            <div v-else class="msg-thumb-placeholder">
                <span class="msg-thumb-initials">{{ initials }}</span>
            </div>
        </div>
        <div class="msg-content">
            <div class="msg-name">{{ participants }}</div>
            <div class="msg-preview">
                <b>{{ lastMessageUsername }}:</b>
                <v-icon v-if="previewIcon" size="13" class="msg-preview-icon">{{ previewIcon }}</v-icon>
                {{ previewText }}
            </div>
        </div>
        <span v-if="!withMenu" class="msg-time-badge">{{ timeAgo }}</span>
        <v-menu v-else offset-y left>
            <template v-slot:activator="{ on, attrs }">
                <v-btn icon small v-bind="attrs" v-on="on" class="msg-menu-btn" @click.stop>
                    <v-icon size="18">mdi-dots-vertical</v-icon>
                </v-btn>
            </template>
            <v-list dense color="surface">
                <v-list-item link @click="$emit('click:delete')">
                    <v-list-item-title>{{ $t('common.delete') }}</v-list-item-title>
                </v-list-item>
            </v-list>
        </v-menu>
    </div>
</template>

<script>

import { getInitials } from '@/utils/Common'

const PREVIEW_LABELS = {
    audio: 'common.message_audio',
    attachment: 'common.message_attachment',
    attachment_video: 'common.message_video',
}

const PREVIEW_ICONS = {
    audio: 'mdi-microphone',
    attachment: 'mdi-paperclip',
    attachment_video: 'mdi-play-circle-outline',
}

export default {
    props: {
        chat: {
            type: Object,
            required: true
        },
        isStudentChats: {
            type: Boolean,
            default: false,
        },
        withMenu: {
            type: Boolean,
            default: false,
        },
        isCompanyChat: {
            type: Boolean,
            default: false,
        }
    },
    computed: {
        participants(vm = this) {
            if (!vm.isStudentChats && vm.accountType !== 'student' && !vm.isCompanyChat) return vm.chat?.name || ''
            let all = vm.chat.participants.slice()
            let meIndex = all.findIndex(participant => participant.objectID === vm.currentUser?.uid)
            if (meIndex !== -1) all.splice(meIndex, 1)
            return all.map(usr => usr.name || usr.email).join(', ')
        },
        client(vm = this) {
            return vm.chat.participants.find(participant => participant.objectID !== vm.currentUser?.uid)
        },
        hasAvatar(vm = this) {
            return !!vm.client?.avatar
        },
        avatar(vm = this) {
            return vm.client?.avatar || vm.imgPlaceholder
        },
        initials(vm = this) {
            return getInitials(vm.participants)
        },
        lastMessageUsername(vm = this) {
            return vm.lastMessage?.from?.name || vm.$tc('common.last', 2)
        },
        lastMessage(vm = this) {
            return vm.chat?.lastMessage
        },
        previewIcon(vm = this) {
            return PREVIEW_ICONS[vm.lastMessage?.type] || ''
        },
        previewText(vm = this) {
            const message = vm.lastMessage
            if (!message) return ''
            const label = PREVIEW_LABELS[message.type] ? vm.$t(PREVIEW_LABELS[message.type]) : ''
            if (message.type === 'audio') return label
            const text = String(message.message || '')
                .replace(/<[^>]*>/g, ' ')
                .replace(/\s+/g, ' ')
                .trim()
            if (!text) return label
            return label ? `${label}: ${text}` : text
        },
        lastMessageNotRead(vm = this) {
            if (vm.lastMessage?.from?.objectID === vm.currentUser?.uid) return false
            return vm.lastMessage && !vm.lastMessage.read
        },
        backgroundCard(vm = this) {
            return vm.accountType !== 'student' ? vm.chat?.color || vm.lastMessage?.color || '' : ''
        },
        cardClass(vm = this) {
            return {
                'msg-card--unread': vm.lastMessageNotRead,
                'msg-card--read': !vm.lastMessageNotRead,
            }
        },
        cardStyle(vm = this) {
            if (vm.lastMessageNotRead && vm.backgroundCard) return { background: vm.backgroundCard }
            return {}
        },
        timeAgo(vm = this) {
            const ts = vm.lastMessage?.timestamp
            if (!ts) return ''
            const date = ts.toDate ? ts.toDate() : (ts.seconds ? new Date(ts.seconds * 1000) : ts)
            const m = vm.$moment(date)
            if (!m.isValid()) return ''
            const now = vm.$moment()
            const minutes = now.diff(m, 'minutes')
            if (minutes < 60) return `${Math.max(minutes, 1)}m`
            const hours = now.diff(m, 'hours')
            if (hours < 24) return `${hours}h`
            if (now.diff(m, 'hours') < 24 * 7) return m.format('ddd')
            return m.format('DD/MM')
        }
    },
    methods: {
        showChat() {
            const vm = this
            vm.$emit('click')
        }
    },
}
</script>

<style scoped>
.msg-card {
    display: flex;
    align-items: stretch;
    border-radius: 10px;
    overflow: hidden;
    cursor: pointer;
    transition: opacity 0.12s, transform 0.12s;
    height: 72px;
    border: 1px solid transparent;
    position: relative;
}

.msg-card:hover {
    opacity: 0.88;
    transform: translateY(-1px);
}

.msg-card--unread {
    background: linear-gradient(135deg, rgba(51, 153, 153, 0.55) 0%, rgba(102, 204, 204, 0.4) 100%);
}

.msg-card--read {
    background: var(--v-surface2-base);
    border-color: var(--v-outline-base);
}

.msg-thumb {
    width: 72px;
    flex-shrink: 0;
    position: relative;
    overflow: hidden;
    background: var(--v-surface2-base);
}

.msg-thumb img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}

.msg-thumb-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, rgba(51, 153, 153, 0.3) 0%, rgba(102, 204, 204, 0.2) 100%);
}

.msg-thumb-initials {
    font-size: 18px;
    font-weight: 800;
    letter-spacing: 0.02em;
    color: rgba(255, 255, 255, 0.8);
}

.msg-content {
    flex: 1;
    min-width: 0;
    padding: 10px 12px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 3px;
}

.msg-name {
    font-size: 13px;
    font-weight: 700;
    color: var(--v-txtPrimary-base);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.msg-preview {
    font-size: 11px;
    color: var(--v-txtSecondary-base);
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    line-height: 1.45;
}

.msg-card--unread .msg-name {
    color: #fff;
}

.msg-card--unread .msg-preview {
    color: rgba(255, 255, 255, 0.82);
}

.msg-preview-icon {
    color: inherit !important;
    vertical-align: -2px;
    margin-right: 1px;
}

.msg-time-badge {
    font-size: 10px;
    color: var(--v-txtMuted-base);
    padding: 6px 8px 0 0;
    flex-shrink: 0;
    align-self: flex-start;
}

.msg-card--unread .msg-time-badge {
    color: rgba(255, 255, 255, 0.6);
}

.msg-menu-btn {
    position: absolute;
    top: 2px;
    right: 2px;
}

.msg-card--unread .msg-menu-btn .v-icon {
    color: rgba(255, 255, 255, 0.8);
}
</style>
